import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { PayoutDetailApiAdapter } from '../adapters';
import { PayoutItemsFilters } from '../models';

interface UsePayoutItemsProps {
  payoutId: number;
  enabled?: boolean;
}

const PER_PAGE = 15;

// Mapeo de campos de AG-Grid a campos de API
const FIELD_TO_API_SORT: Record<string, string> = {
  id: 'id',
  'order.ref': 'ref',
  'order.tracking_number': 'tracking_number',
  'order.shop.name': 'shop_id',
  'order.courier.name': 'courier_id',
  payout_type: 'payout_type',
  amount: 'amount',
  description: 'description',
};

export const usePayoutItems = ({ payoutId, enabled = true }: UsePayoutItemsProps) => {
  const httpClient = createHttpClient(apiClientWH);
  const payoutDetailAdapter = new PayoutDetailApiAdapter(httpClient);

  const [filters, setFilters] = useState<Omit<PayoutItemsFilters, 'page'>>({
    per_page: PER_PAGE,
  });

  const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Convertir sorting de AG-Grid a filtros de API
  const apiFilters = useMemo(() => {
    const apiField = sorting?.field === 'amount' ? FIELD_TO_API_SORT[sorting.field] || sorting.field : undefined;
    const apiDirection = sorting?.field === 'amount' ? sorting?.direction : undefined;

    const baseFilters = { ...filters };
    delete (baseFilters as PayoutItemsFilters).sort_by;
    delete (baseFilters as PayoutItemsFilters).sort_direction;

    return {
      ...baseFilters,
      ...(apiField ? { sort_by: apiField } : {}),
      ...(apiDirection ? { sort_direction: apiDirection } : {}),
    };
  }, [filters, sorting]);

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useInfiniteQuery({
      queryKey: ['cod-payout-items', payoutId, apiFilters, sorting],
      queryFn: ({ pageParam = 1 }) =>
        payoutDetailAdapter.getPayoutItems(payoutId, { ...apiFilters, page: pageParam as number }),
      getNextPageParam: (lastPage) => {
        const meta = lastPage?.meta ?? {};
        const currentPage = meta.current_page ?? (meta as { currentPage?: number }).currentPage;
        const lastPageNum = meta.last_page ?? (meta as { lastPage?: number }).lastPage;
        if (currentPage != null && lastPageNum != null && currentPage < lastPageNum) {
          return currentPage + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      enabled: enabled && !!payoutId,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    });

  const items = useMemo(() => data?.pages.flatMap((page) => page?.data ?? []) ?? [], [data?.pages]);
  const meta = data?.pages?.[0]?.meta;
  const links = data?.pages?.[0]?.links;

  // Detectar si hay filtros activos (excluyendo paginación y ordenamiento)
  const hasFilters = useMemo(() => {
    return !!(filters.ref || filters.tracking_number || filters.shop_id || filters.payout_type);
  }, [filters]);

  // Query para todos los items sin filtros (resumen) - solo cuando hay filtros activos
  const { data: allItemsData } = useQuery({
    queryKey: ['cod-payout-items-all', payoutId],
    queryFn: () =>
      payoutDetailAdapter.getPayoutItems(payoutId, {
        per_page: PER_PAGE,
        page: 1,
      }),
    enabled: enabled && !!payoutId && hasFilters,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const changePageSize = (per_page: number) => {
    setFilters((prev) => ({ ...prev, per_page }));
  };

  const changeSorting = (sort_by: string, sort_direction: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sort_by, sort_direction }));
  };

  const handleSortingChange = (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    setSorting(newSorting);
    setFilters((prev) => {
      const next = { ...prev };
      delete (next as PayoutItemsFilters).sort_by;
      delete (next as PayoutItemsFilters).sort_direction;
      return next;
    });
  };

  const updateFilters = (newFilters: Partial<PayoutItemsFilters>) => {
    const rest = { ...newFilters };
    delete rest.page;
    setFilters((prev) => ({ ...prev, ...rest }));
  };

  const resetFilters = () => {
    setFilters({
      per_page: PER_PAGE,
    });
    setSorting(null);
  };

  const summaryItems = useMemo(() => {
    if (hasFilters && allItemsData?.data) {
      return allItemsData.data;
    }
    return items;
  }, [hasFilters, allItemsData, items]);

  return {
    items,
    summaryItems,
    meta,
    links,
    isLoading: isLoading || isPending,
    isFetching,
    error: error ? (error as Error).message : null,
    refetch,
    changePageSize,
    changeSorting,
    handleSortingChange,
    updateFilters,
    resetFilters,
    filters,
    sorting,
    hasFilters,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
